import { Card, Col, Input, Row, message } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { RotateCcw, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axios/axios-instance";
import ActionButton from "../../components/common/buttons/ActionButton";
import CancelButton from "../../components/common/buttons/CancelButton";
import AreaChart from "../../components/chart/area-chart/AreaChart";
import DateTimePickerCommon from "../../components/common/DateTimePickerCommon";
import DropdownCommon from "../../components/common/DropdownCommon";
import { ChartRequest } from "../../utils/models";
import { formatNumber } from "../../utils/utils";

type DashboardCardKey = "revenue" | "customer" | "owner" | "order";
type ChartKey = "revenue" | "customer" | "outlet" | "order";
type DashboardChartType = "line" | "bar";

interface DashboardCardState {
  revenue: { total: number };
  customer: { total: number };
  owner: { total: number };
  order: { total: number };
}

interface DashboardChartItem {
  total: number[];
  date: string[];
}

type DashboardChartState = Record<ChartKey, DashboardChartItem>;

const DASHBOARD_CHART_COLORS = ["#F04770", "#15B79F", "#FB9C0C", "#108AB1"];

const initialDashboardCardState: DashboardCardState = {
  revenue: { total: 0 },
  customer: { total: 0 },
  owner: { total: 0 },
  order: { total: 0 },
};

const initialChartData: DashboardChartState = {
  order: {
    total: [],
    date: [],
  },
  revenue: {
    total: [],
    date: [],
  },
  customer: {
    total: [],
    date: [],
  },
  outlet: {
    total: [],
    date: [],
  },
};

const Dashboard = () => {
  const { t } = useTranslation();
  const defaultStartTime = dayjs().subtract(7, "day");
  const defaultEndTime = dayjs();
  const allTimeStart = dayjs(0);
  const [dashboardCardState, setDashboardCardState] = useState<DashboardCardState>(
    initialDashboardCardState
  );
  const [chartData, setChartData] = useState<DashboardChartState>(initialChartData);
  const [startTime, setStartTime] = useState<Dayjs | null>(defaultStartTime);
  const [endTime, setEndTime] = useState<Dayjs | null>(defaultEndTime);
  const [searchText, setSearchText] = useState("");
  const [messageApi, contextHolder] = message.useMessage();
  const [request, setRequest] = useState<ChartRequest>({
    current_time: Date.now(),
    start_time: defaultStartTime.valueOf(),
    end_time: defaultEndTime.valueOf(),
  });
  const [chartType, setChartType] = useState<DashboardChartType>("line");

  const setStartTimeFunc = (value: Dayjs | null) => {
    if (!value || !dayjs(value).isValid()) {
      return;
    }

    const nextStartTime = dayjs(value);
    if (endTime && nextStartTime.valueOf() > endTime.valueOf()) {
      messageApi.open({
        type: "error",
        content: t("dashboard.validation.end_date_error"),
      });
      return;
    }

    setStartTime(nextStartTime);
    setRequest((previous) => ({
      ...previous,
      start_time: nextStartTime.valueOf(),
    }));
  };

  const setEndTimeFunc = (value: Dayjs | null) => {
    if (!value || !dayjs(value).isValid()) {
      return;
    }

    const nextEndTime = dayjs(value);
    if (startTime && nextEndTime.valueOf() < startTime.valueOf()) {
      messageApi.open({
        type: "error",
        content: t("dashboard.validation.end_date_error"),
      });
      return;
    }

    setEndTime(nextEndTime);
    setRequest((previous) => ({
      ...previous,
      end_time: nextEndTime.valueOf(),
    }));
  };

  const initDashboardData = async () => {
    const params = new URLSearchParams({
      currentTime: `${request.current_time}`,
    });

    if (request.search) {
      params.set("search", request.search);
    }

    await axiosInstance
      .get(`/admin/dashboard?${params.toString()}`)
      .then((response: any) => {
        if (response.status === 200) {
          setDashboardCardState({
            revenue: {
              total: response.data.data.revenue_response?.total ?? 0,
            },
            customer: {
              total: response.data.data.customer_response?.total ?? 0,
            },
            owner: {
              total: response.data.data.outlet_response?.total ?? 0,
            },
            order: {
              total: response.data.data.booking_response?.total ?? 0,
            },
          });
        }
      })
      .catch((err) => console.log(" === err: ", err));
  };

  const getDataForChart = async () => {
    await axiosInstance
      .post("/admin/dashboard/chart", request)
      .then((res) => {
        setChartData({
          order: {
            total: res.data.data.booking_chart_response?.data ?? [],
            date: res.data.data.booking_chart_response?.times ?? [],
          },
          revenue: {
            total: res.data.data.revenue_chart_response?.data ?? [],
            date: res.data.data.revenue_chart_response?.times ?? [],
          },
          customer: {
            total: res.data.data.customer_chart_response?.data ?? [],
            date: res.data.data.customer_chart_response?.times ?? [],
          },
          outlet: {
            total: res.data.data.outlet_chart_response?.data ?? [],
            date: res.data.data.outlet_chart_response?.times ?? [],
          },
        });
      })
      .catch((error) => {
        console.log("=== get chart data error: ", error);
      });
  };

  const handleChangeChartType = (value: string) => {
    if (value === "line" || value === "bar") {
      setChartType(value);
    }
  };

  const handleSearch = () => {
    const nextSearch = searchText.trim();
    setRequest((previous) => ({
      ...previous,
      current_time: Date.now(),
      search: nextSearch || undefined,
    }));
  };

  const handleClear = () => {
    const now = dayjs();
    setSearchText("");
    setStartTime(allTimeStart);
    setEndTime(now);
    setRequest({
      current_time: now.valueOf(),
      start_time: allTimeStart.valueOf(),
      end_time: now.valueOf(),
      search: undefined,
    });
  };

  const chartTypeOptions = useMemo(
    () => [
      { key: "line", value: t("dashboard.chart.line") },
      { key: "bar", value: t("dashboard.chart.bar") },
    ],
    [t]
  );

  const cardTitle = [
    {
      title: t("dashboard.cards.revenue"),
      key: "revenue" as DashboardCardKey,
      icon: "/icon/revenue.svg",
      bgColor: "bg-[#F04770]",
    },
    {
      title: t("dashboard.cards.customers"),
      key: "customer" as DashboardCardKey,
      icon: "/icon/customer.svg",
      bgColor: "bg-[#15b79f]",
    },
    {
      title: t("dashboard.cards.owners"),
      key: "owner" as DashboardCardKey,
      icon: "/icon/owner.svg",
      bgColor: "bg-[#fb9c0c]",
    },
    {
      title: t("dashboard.cards.bookings"),
      key: "order" as DashboardCardKey,
      icon: "/icon/order.svg",
      bgColor: "bg-[#108AB1]",
    },
  ];

  const chartDates = useMemo(() => {
    const dates = Object.values(chartData).flatMap((item) => item.date);
    return Array.from(new Set(dates)).sort(
      (firstDate, secondDate) => dayjs(firstDate).valueOf() - dayjs(secondDate).valueOf()
    );
  }, [chartData]);

  const getChartSeriesData = (key: ChartKey) => {
    const totalsByDate = new Map(
      chartData[key].date.map((date, index) => [date, chartData[key].total[index] ?? 0])
    );

    return chartDates.map((date) => totalsByDate.get(date) ?? 0);
  };

  const combinedChartSeries = useMemo(
    () => [
      {
        name: t("dashboard.chart.revenue"),
        data: getChartSeriesData("revenue"),
      },
      {
        name: t("dashboard.chart.customers"),
        data: getChartSeriesData("customer"),
      },
      {
        name: t("dashboard.chart.outlet"),
        data: getChartSeriesData("outlet"),
      },
      {
        name: t("dashboard.chart.bookings"),
        data: getChartSeriesData("order"),
      },
    ],
    [chartData, chartDates, t]
  );

  const getDataCard = (key: DashboardCardKey) => formatNumber(dashboardCardState[key].total);

  useEffect(() => {
    getDataForChart();
  }, [request]);

  useEffect(() => {
    initDashboardData();
  }, [request.current_time, request.search]);

  return (
    <div className="dashboard">
      <Row gutter={[12, 12]}>
        {cardTitle.map((itemCard) => (
          <Col span={24} md={12} lg={6} key={itemCard.key}>
            <div
              className={`border-solid border-[1px] rounded-lg border-gray-400 p-4 shadow-card ${itemCard.bgColor}`}
            >
              <div className="flex justify-between mb-4">
                <div className="font-bold text-xl text-white">{itemCard.title}</div>
                <img
                  src={itemCard.icon}
                  alt="card icon"
                  className="w-10 h-10 rounded-full p-2 border border-solid"
                />
              </div>
              <div className="text-white text-sm">{t("dashboard.cards.total")}</div>
              <div className="font-bold text-white text-3xl mt-1 break-words">
                {getDataCard(itemCard.key)}
              </div>
            </div>
          </Col>
        ))}
      </Row>

      <Row gutter={[12, 12]} className="mt-4">
        <Col span={24} md={12} lg={6}>
          <div>{t("dashboard.filter.search")}</div>
          <Input
            className="h-[40px]"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onPressEnter={handleSearch}
            placeholder={t("dashboard.filter.search_placeholder")}
            allowClear
          />
        </Col>
        <Col span={24} md={8} lg={6}>
          <div id="dashboard-chart-type-label">{t("dashboard.chart.chart_type")}</div>
          <DropdownCommon
            dropdownItem={chartTypeOptions}
            onSelected={handleChangeChartType}
          />
        </Col>
        <Col span={24} md={8} lg={6}>
          <div>{t("dashboard.chart.start_time")}</div>
          <DateTimePickerCommon
            onChange={(newValue: Dayjs | null) => setStartTimeFunc(newValue)}
            defaultValue={startTime ?? defaultStartTime}
            value={startTime}
          />
        </Col>
        <Col span={24} md={8} lg={6}>
          <div>{t("dashboard.chart.end_time")}</div>
          <DateTimePickerCommon
            onChange={(newValue: Dayjs | null) => setEndTimeFunc(newValue)}
            defaultValue={endTime ?? defaultEndTime}
            value={endTime}
          />
        </Col>
        <Col span={24} className="flex gap-2 justify-end">
          <ActionButton startIcon={<Search size={16} />} onClick={handleSearch}>
            {t("dashboard.filter.search_button")}
          </ActionButton>
          <CancelButton startIcon={<RotateCcw size={16} />} onClick={handleClear}>
            {t("dashboard.filter.clear_button")}
          </CancelButton>
        </Col>
      </Row>

      <Row gutter={[12, 12]} className="mt-5">
        <Col span={24}>
          <Card className="w-full">
            <div className="title mb-2">{t("dashboard.chart.overview")}</div>
            <AreaChart
              date={chartDates}
              series={combinedChartSeries}
              type={chartType}
              colors={DASHBOARD_CHART_COLORS}
            />
          </Card>
        </Col>
      </Row>
      {contextHolder}
    </div>
  );
};

export default Dashboard;
